// Where the SHARED fragment objects live, given a manifest's base url.
//
// R2 has two roots. Tiles and manifests are project-scoped:
//
//   uploads/{projectId}/manifest.json.gz
//   uploads/{projectId}/tiles/tile_*.json.gz
//
// ...while fragment GLBs are global and content-addressed:
//
//   uploads/fragments/{hash}.glb.gz
//
// They are global because the same (geometry, material) pair recurs
// across projects and across companies; putting them under the project
// prefix would force a re-upload per project and make the cache useless.
//
// So the fetcher resolves two different roots from one base url, and
// getting the fragment one wrong does not raise an error anywhere. The
// manifest loads, the sidecars load, the model tree populates, the counts
// look right, and the scene is empty. It reads as a broken viewer rather
// than a base url that is wrong by a couple of segments.
//
// Lives in its own module, free of the three.js import GLBFetcher carries,
// so it can be tested without pulling an ESM loader through a transform
// that is not configured for node_modules.

const MARKER = '/uploads';

/**
 * Walk a manifest base url up to the `/uploads` bucket prefix.
 *
 * Handles both layouts:
 *   .../uploads/{projectId}                          -> .../uploads
 *   .../uploads/{projectId}/versions/{exportVersion} -> .../uploads
 *
 * The second is why this is not simply "strip one segment", which is what
 * it used to be: versioned exports put the manifest two segments deeper,
 * so stripping one lands on `.../versions` and every fragment 404s.
 *
 * Falls back to stripping one segment when there is no `/uploads` marker,
 * which preserves the old behaviour for any other host layout.
 */
export function deriveFragmentBase(baseUrl) {
  const s = String(baseUrl || '').replace(/\/+$/, '');
  // FIRST occurrence: `/uploads` is the bucket prefix and appears once at
  // the top of the path. Matching the last would return the whole string
  // for a path that ends in it, sending every fragment one level too deep.
  const i = s.indexOf(MARKER);
  if (i >= 0) return s.slice(0, i + MARKER.length);
  return s.replace(/\/[^/]+$/, '');
}

export default deriveFragmentBase;
